import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowRight, SpinnerGap } from '@phosphor-icons/react';
import { authAPI } from '@/utils/api';
import { setUser, getUser } from '@/utils/auth';
import { toast } from 'sonner';

/**
 * Post-checkout landing page (both flows land here):
 *   1. Trial-to-paid conversion (via /billing/convert-trial-to-paid)
 *   2. Brand-new signup (via /create-checkout-session)
 *
 * On mount we call /auth/me to refresh the authenticated user snapshot
 * (localStorage) so `subscription_status` flips to "active" and every
 * download button in the app unlocks immediately.
 *
 * We poll a few times because the Stripe webhook that flips the status
 * server-side is asynchronous; the direct trial-modify path already
 * pre-flips the row optimistically, but a fresh checkout may need
 * a couple of seconds for `invoice.payment_succeeded` to arrive.
 */
const AUTH_BG = '#0F1117';
const AUTH_CARD = '#1E222D';
const AUTH_BORDER = 'rgba(255,255,255,0.08)';

const MAX_POLLS = 12;   // 12 × 1s = 12s upper bound
const POLL_MS = 1000;

const SubscribeSuccessPage = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('refreshing'); // refreshing | active | timeout
  const [attempts, setAttempts] = useState(0);
  const [user, setLocalUser] = useState(getUser());

  useEffect(() => {
    let cancelled = false;
    let pollTimer = null;

    const refreshOnce = async () => {
      try {
        const resp = await authAPI.getMe();
        if (cancelled) return;
        const fresh = resp?.data;
        if (fresh) {
          setUser(fresh);
          setLocalUser(fresh);
          if (fresh.is_internal === true || fresh.subscription_status === 'active') {
            setStatus('active');
            return true;
          }
        }
      } catch (err) {
        // silent — we'll retry
      }
      return false;
    };

    const loop = async () => {
      const ok = await refreshOnce();
      if (cancelled) return;
      if (ok) return;
      setAttempts((n) => {
        const next = n + 1;
        if (next >= MAX_POLLS) {
          setStatus('timeout');
        } else {
          pollTimer = setTimeout(loop, POLL_MS);
        }
        return next;
      });
    };

    loop();
    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, []);

  useEffect(() => {
    if (status === 'active') {
      toast.success('Subscription active. Downloads unlocked.');
    }
  }, [status]);

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-12 relative overflow-hidden"
      style={{ backgroundColor: AUTH_BG }}
      data-testid="subscribe-success-page"
    >
      {/* Ambient emerald glow */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div
          className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full opacity-20 blur-3xl"
          style={{ background: 'radial-gradient(closest-side, #10b981, transparent)' }}
        />
        <div
          className="absolute top-1/2 -right-40 w-[520px] h-[520px] rounded-full opacity-10 blur-3xl"
          style={{ background: 'radial-gradient(closest-side, #34d399, transparent)' }}
        />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative w-full max-w-md rounded-2xl border p-8 shadow-2xl text-center space-y-5"
        style={{ backgroundColor: AUTH_CARD, borderColor: AUTH_BORDER }}
      >
        {status === 'refreshing' && (
          <>
            <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center shadow-lg shadow-emerald-500/25">
              <SpinnerGap size={32} weight="bold" className="text-white animate-spin" />
            </div>
            <h1 className="text-2xl font-bold text-white tracking-tight" data-testid="subscribe-success-title">
              Finalizing your subscription…
            </h1>
            <p className="text-sm text-slate-400 leading-relaxed">
              We&rsquo;re confirming your payment with Stripe and unlocking your downloads.
            </p>
          </>
        )}

        {status === 'active' && (
          <>
            <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center shadow-lg shadow-emerald-500/25">
              <CheckCircle size={32} weight="fill" className="text-white" />
            </div>
            <h1
              className="text-2xl font-bold text-white tracking-tight"
              data-testid="subscribe-success-title"
            >
              You&rsquo;re active.
            </h1>
            <p
              className="text-sm text-slate-300 leading-relaxed"
              data-testid="subscribe-success-body"
            >
              {user?.name ? `Welcome, ${user.name}. ` : ''}
              Every asset you&rsquo;ve created is unlocked. Downloads, exports, and every future asset are yours.
            </p>
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              data-testid="subscribe-success-cta"
              className="w-full py-3.5 bg-gradient-to-br from-emerald-500 to-green-600 hover:shadow-lg hover:shadow-emerald-500/25 text-white font-semibold rounded-xl transition-all focus:outline-none focus:ring-2 focus:ring-emerald-500/40 flex items-center justify-center gap-2"
            >
              Return to dashboard
              <ArrowRight size={18} weight="bold" />
            </button>
          </>
        )}

        {status === 'timeout' && (
          <>
            <div className="w-16 h-16 mx-auto rounded-2xl bg-amber-500/20 border border-amber-500/30 flex items-center justify-center">
              <SpinnerGap size={32} weight="bold" className="text-amber-300" />
            </div>
            <h1 className="text-2xl font-bold text-white tracking-tight" data-testid="subscribe-success-title">
              Almost there…
            </h1>
            <p className="text-sm text-slate-400 leading-relaxed">
              Your payment is processing with Stripe. Refresh in a few seconds and
              your downloads will be unlocked. If it doesn&rsquo;t clear within a minute,
              contact support.
            </p>
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => window.location.reload()}
                data-testid="subscribe-success-retry"
                className="w-full py-3 bg-gradient-to-br from-emerald-500 to-green-600 hover:shadow-lg hover:shadow-emerald-500/25 text-white font-semibold rounded-xl transition-all focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
              >
                Refresh
              </button>
              <Link
                to="/dashboard"
                className="text-sm text-slate-400 hover:text-slate-200 focus:outline-none focus:underline"
              >
                Return to dashboard
              </Link>
            </div>
          </>
        )}

        {status === 'refreshing' && attempts > 0 && (
          <p className="text-xs text-slate-500">Attempt {attempts + 1}/{MAX_POLLS}</p>
        )}
      </motion.div>
    </div>
  );
};

export default SubscribeSuccessPage;
